import { Button, Input, Segmented, Table, Tabs, Tag, Tooltip } from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	EyeOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	countTravel,
	loadAllTravel,
	reviewTravel,
} from "../../redux/rtk/features/travel/travelSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { GreenLinkBtn } from "../UI/AllLinkBtn";
import BtnAllSvg from "../UI/Button/btnAllSvg";
import PageTitle from "../page-header/PageHeader";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

const { TabPane } = Tabs;

// Travel Requests Table Component
function TravelRequestsTable({ list, total, status, onStatusChange, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const staffList = useSelector((state) => state.users?.list || []);
	const [searchEmployee, setSearchEmployee] = useState("");

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const handleApprove = async (id) => {
		const resp = await dispatch(
			reviewTravel({
				id,
				values: { approvalStatus: "APPROVED" },
			})
		);
		if (resp.payload?.message === "success") {
			dispatch(loadAllTravel());
		}
	};

	const handleReject = async (id) => {
		const resp = await dispatch(
			reviewTravel({
				id,
				values: { approvalStatus: "REJECTED" },
			})
		);
		if (resp.payload?.message === "success") {
			dispatch(loadAllTravel());
		}
	};

	const columns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "From",
			dataIndex: "fromLocation",
			key: "fromLocation",
		},
		{
			title: "To",
			dataIndex: "toLocation",
			key: "toLocation",
		},
		{
			title: "Travel Date",
			dataIndex: "travelDate",
			key: "travelDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Return Date",
			dataIndex: "returnDate",
			key: "returnDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Purpose",
			dataIndex: "purpose",
			key: "purpose",
			render: (purpose) =>
				purpose
					? purpose.substring(0, 20) + (purpose.length > 20 ? "..." : "")
					: "-",
		},
		{
			title: "Estimated Cost (₹)",
			dataIndex: "estimatedCost",
			key: "estimatedCost",
			render: (cost) => {
				if (!cost) return "-";
				return `₹ ${Number(cost).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Travel Mode",
			dataIndex: "travelMode",
			key: "travelMode",
		},
		{
			title: "Status",
			dataIndex: "approvalStatus",
			key: "approvalStatus",
			render: (approvalStatus) => {
				if (approvalStatus === "APPROVED") {
					return <Tag color="green">Approved</Tag>;
				} else if (approvalStatus === "REJECTED") {
					return <Tag color="red">Rejected</Tag>;
				} else {
					return <Tag color="orange">Pending</Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id, approvalStatus }) => {
				const travelId = id;
				return (
					<div style={{ display: "flex", gap: "8px" }}>
						<ViewBtn
							path={`/admin/travel/${travelId}`}
							text="View"
							icon={<BtnViewSvg />}
						/>
						{approvalStatus === "PENDING" && (
							<>
								<Tooltip title="Approve">
									<Button
										type="primary"
										icon={<CheckCircleOutlined />}
										size="small"
										onClick={() => handleApprove(travelId)}
									>
										Approve
									</Button>
								</Tooltip>
								<Tooltip title="Reject">
									<Button
										type="primary"
										danger
										icon={<CloseCircleOutlined />}
										size="small"
										onClick={() => handleReject(travelId)}
									>
										Reject
									</Button>
								</Tooltip>
							</>
						)}
					</div>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.TravelId || index,
			serial: index + 1,
			employeeName: mapEmployeeName(i.employeeId || i.EmployeeId),
		}));

	const filteredData = addKeys(list).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		const matchesSearch = employeeName.includes(searchEmployee.toLowerCase());
		const matchesStatus =
			status === "all" ||
			(item.approvalStatus || "").toUpperCase() === status.toUpperCase();
		return matchesSearch && matchesStatus;
	});

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">Travel Requests</h4>
				</div>
				<div className="flex justify-end mr-4 gap-2">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
					<GreenLinkBtn>
						<button onClick={onRefresh}>
							<BtnAllSvg size={15} title={"ALL"} />
						</button>
					</GreenLinkBtn>
					<Segmented
						className="text-center rounded"
						size="middle"
						value={status}
						onChange={onStatusChange}
						options={[
							{
								label: <span>Pending</span>,
								value: "PENDING",
							},
							{
								label: <span>Approved</span>,
								value: "APPROVED",
							},
							{
								label: <span>Rejected</span>,
								value: "REJECTED",
							},
						]}
					/>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>

				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<Input
						placeholder="Search by Employee Name"
						value={searchEmployee}
						onChange={(e) => setSearchEmployee(e.target.value)}
						style={{ width: 200 }}
					/>
				</div>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(loadAllTravel({ page, limit }));
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

// Approved Travel Table Component
function ApprovedTravelTable({ list, total, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const staffList = useSelector((state) => state.users?.list || []);
	const [searchEmployee, setSearchEmployee] = useState("");

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const columns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "From",
			dataIndex: "fromLocation",
			key: "fromLocation",
		},
		{
			title: "To",
			dataIndex: "toLocation",
			key: "toLocation",
		},
		{
			title: "Travel Date",
			dataIndex: "travelDate",
			key: "travelDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Return Date",
			dataIndex: "returnDate",
			key: "returnDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Estimated Cost (₹)",
			dataIndex: "estimatedCost",
			key: "estimatedCost",
			render: (cost) => {
				if (!cost) return "-";
				return `₹ ${Number(cost).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Travel Mode",
			dataIndex: "travelMode",
			key: "travelMode",
		},
		{
			title: "Status",
			dataIndex: "approvalStatus",
			key: "approvalStatus",
			render: () => <Tag color="green">Approved</Tag>,
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id }) => {
				const travelId = id;
				return (
					<ViewBtn
						path={`/admin/travel/${travelId}`}
						text="View"
						icon={<BtnViewSvg />}
					/>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.TravelId || index,
			serial: index + 1,
			employeeName: mapEmployeeName(i.employeeId || i.EmployeeId),
		}));

	const filteredData = addKeys(list).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		return employeeName.includes(searchEmployee.toLowerCase());
	});

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">Approved Travel</h4>
				</div>
				<div className="flex justify-end mr-4">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>

				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<Input
						placeholder="Search by Employee Name"
						value={searchEmployee}
						onChange={(e) => setSearchEmployee(e.target.value)}
						style={{ width: 200 }}
					/>
				</div>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(loadAllTravel({ page, limit }));
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

const TravelStatus = (props) => {
	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState("requests");
	const [status, setStatus] = useState("PENDING");
	const list = useSelector((state) => state.travel?.list || []);
	const total = useSelector((state) => state.travel?.total || 0);

	// Filter approved travel
	const approvedTravel = list.filter(
		(travel) =>
			travel.approvalStatus === "APPROVED" ||
			travel.status === "APPROVED"
	);

	useEffect(() => {
		dispatch(loadAllTravel());
		dispatch(countTravel());
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const handleStatusChange = (value) => {
		setStatus(value);
	};

	const handleRefresh = () => {
		dispatch(loadAllTravel());
	};

	return (
		<div>
		
			<div className="card card-custom">
				<div className="card-body">
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						type="card"
					>
						<TabPane tab="Travel Requests" key="requests">
							<TravelRequestsTable
								list={list}
								total={total}
								status={status}
								onStatusChange={handleStatusChange}
								onRefresh={handleRefresh}
							/>
						</TabPane>
						<TabPane tab="Approved Travel" key="approved">
							<ApprovedTravelTable
								list={approvedTravel}
								total={approvedTravel.length}
								onRefresh={handleRefresh}
							/>
						</TabPane>
					</Tabs>
				</div>
			</div>
		</div>
	);
};

export default TravelStatus;

